import React, { createContext, useContext, useState, useEffect } from 'react';
import { signInWithEmailAndPassword, signOut, onAuthStateChanged, User as FirebaseUser, MultiFactorResolver } from 'firebase/auth';
import { auth, multiFactor, TotpMultiFactorGenerator, getMultiFactorResolver } from '../../config/firebase';
import { userService, UserProfile } from '../../services/api';
import { logger } from '../../utils/logger';
import { SessionTimeoutModal } from './SessionTimeoutModal';
import { useSessionTimeout, TimeoutReason } from '../../hooks/useSessionTimeout';
import { MfaEnrollment } from './MfaEnrollment';
import { ForcePasswordChangeModal } from './ForcePasswordChangeModal';

interface OrganizationInfo {
  id: string;
  name: string;
  type: 'client' | 'bank';
}

interface User {
  id: string;
  name: string;
  email: string;
  role: 'client_user' | 'client_admin' | 'client_fo_user' | 'client_bo_user' | 'client_fo_bo_user' | 'client_ro_user' | 'client_acc_user' | 'bank_admin';
  profile?: UserProfile; // Add backend profile data
  permissions?: string[]; // Add backend permissions
  organization?: OrganizationInfo; // Add organization info
  moduleRoles?: Record<string, string>; // Per-module roles (KAN-256)
}

interface AuthContextType {
  user: User | null;
  loading: boolean;
  login: (email: string, password: string) => Promise<void>;
  completeMfaLogin: (resolver: MultiFactorResolver, otpCode: string) => Promise<void>;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

// Module-level flag to suppress onAuthStateChanged processing during
// temp access auth flow.  Login.tsx signs in with Firebase purely to get
// an ID token — we must prevent AuthContext from setting user state,
// which would trigger the App.tsx redirect to "/" and flash the Layout.
let _suppressAuthState = false;
export function setSuppressAuthState(value: boolean) {
  _suppressAuthState = value;
}

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuth must be used within an AuthProvider');
  }
  return context;
};

/**
 * Determine whether a user has FX module access.
 *
 * Backward-compatible rule:
 *  - No moduleRoles (or empty) → legacy FX user → true
 *  - moduleRoles is non-empty  → FX only if moduleRoles contains "FX"
 */
export function hasFxAccess(user: { moduleRoles?: Record<string, string> } | null | undefined): boolean {
  if (!user) return false;
  if (!user.moduleRoles || Object.keys(user.moduleRoles).length === 0) return true;
  return 'FX' in user.moduleRoles;
}

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [sessionStartTime, setSessionStartTime] = useState<number | null>(null);
  const [timeoutReason, setTimeoutReason] = useState<TimeoutReason | null>(null);
  const [showMfaEnrollment, setShowMfaEnrollment] = useState(false);
  const [mfaEnrollmentForced, setMfaEnrollmentForced] = useState(false);
  const [showForcePasswordChange, setShowForcePasswordChange] = useState(false);

  // Handle session timeout
  const handleSessionTimeout = async (reason: TimeoutReason) => {
    logger.log(`[AuthContext] Session timeout: ${reason}`);
    setTimeoutReason(reason);
    // Logout will trigger onAuthStateChanged which sets user to null
    // This will cause the warning modal to unmount since enabled will be false
    await logout();
  };

  // Session timeout hook
  const { showWarning, timeoutReason: hookTimeoutReason, secondsRemaining, extendSession, hideWarning } = useSessionTimeout({
    onTimeout: handleSessionTimeout,
    sessionStartTime,
    enabled: !!user && !showMfaEnrollment && !showForcePasswordChange,
  });

  // Wrap logout to hide warning modal first
  const handleLogoutFromModal = async () => {
    hideWarning(); // Hide modal immediately
    await logout();
  };

  useEffect(() => {
    // Listen for authentication state changes
    const unsubscribe = onAuthStateChanged(auth, async (firebaseUser) => {
      // Skip processing when temp access auth is in progress (KAN-204).
      // signInWithEmailAndPassword fires this listener, but we don't
      // want to set user state — just need the ID token.
      if (_suppressAuthState) {
        return;
      }
      if (firebaseUser) {
        logger.log('[AuthContext] Firebase user logged in:', firebaseUser.email);
        try {
          // Get user profile from backend
          logger.log('[AuthContext] Fetching profile from backend...');
          const profileResponse = await userService.getMyProfile();
          const profile = profileResponse.data;
          logger.log('[AuthContext] Backend profile:', {
            email: profile?.email,
            primaryRole: profile?.primaryRole,
            orgType: profile?.organization?.type,
            orgId: profile?.organization?.id
          });

          // Map Firebase user with backend profile data
          const mappedUser = mapFirebaseUserToUser(firebaseUser, profile);
          logger.log('[AuthContext] Mapped user:', {
            email: mappedUser.email,
            role: mappedUser.role,
            orgType: mappedUser.organization?.type
          });
          setUser(mappedUser);

          // Start session tracking — preserve across page refreshes via sessionStorage
          const existingStart = sessionStorage.getItem('sessionStartTime');
          if (existingStart) {
            setSessionStartTime(Number(existingStart));
          } else {
            const now = Date.now();
            sessionStorage.setItem('sessionStartTime', String(now));
            setSessionStartTime(now);
          }

          // Check if user needs forced MFA enrollment
          if (profile?.mfaRequired) {
            const enrolledFactors = multiFactor(firebaseUser).enrolledFactors;
            const hasTOTP = enrolledFactors.some(f => f.factorId === 'totp');
            if (!hasTOTP) {
              logger.log('[AuthContext] User requires MFA enrollment (forced)');
              setMfaEnrollmentForced(true);
              setShowMfaEnrollment(true);
            } else {
              setShowMfaEnrollment(false);
            }
          } else {
            setShowMfaEnrollment(false);

            // Auto-unenroll TOTP if admin has disabled MFA but factor is still
            // enrolled on Firebase Auth (Python Admin SDK cannot unenroll server-side).
            if (!profile?.mfaRequired) {
              const enrolledFactors = multiFactor(firebaseUser).enrolledFactors;
              const totpFactor = enrolledFactors.find(f => f.factorId === 'totp');
              if (totpFactor) {
                logger.log('[AuthContext] Admin disabled MFA — auto-unenrolling orphaned TOTP factor');
                try {
                  await multiFactor(firebaseUser).unenroll(totpFactor);
                  logger.log('[AuthContext] TOTP factor unenrolled successfully');
                } catch (unenrollError) {
                  logger.error('[AuthContext] Failed to auto-unenroll TOTP factor:', unenrollError);
                }
              }
            }
          }
        } catch (error) {
          logger.error('[AuthContext] Failed to load user profile from backend:', error);
          // Fallback to Firebase-only user
          const mappedUser = mapFirebaseUserToUser(firebaseUser);
          logger.log('[AuthContext] Using fallback user:', {
            email: mappedUser.email,
            role: mappedUser.role
          });
          setUser(mappedUser);
        }
      } else {
        setUser(null);
        setSessionStartTime(null);
        sessionStorage.removeItem('sessionStartTime');
      }
      setLoading(false);
    });

    return () => unsubscribe();
  }, []);

  // Sequential gating: show forced password change only after MFA is resolved
  useEffect(() => {
    if (
      user?.profile?.mustChangePassword &&
      !showMfaEnrollment
    ) {
      setShowForcePasswordChange(true);
    } else {
      setShowForcePasswordChange(false);
    }
  }, [user?.profile?.mustChangePassword, showMfaEnrollment]);

  // Helper function to map Firebase user to our User interface
  const mapFirebaseUserToUser = (firebaseUser: FirebaseUser, profile?: UserProfile): User => {
    // Determine role based on backend profile or email domain fallback
    type UserRole = 'client_user' | 'client_admin' | 'client_fo_user' | 'client_bo_user' | 'client_fo_bo_user' | 'client_ro_user' | 'client_acc_user' | 'bank_admin';
    let role: UserRole = 'client_user';

    // Extract role name from primaryRole path (e.g., "/roles/client_fo_user" -> "client_fo_user")
    const extractRoleFromPath = (rolePath: string): string => {
      const parts = rolePath.split('/');
      return parts[parts.length - 1]; // Get last segment
    };

    if (profile?.organization?.type === 'bank') {
      role = 'bank_admin';
    } else if (profile?.primaryRole) {
      // Extract role from primaryRole path and validate it
      const extractedRole = extractRoleFromPath(profile.primaryRole);
      const validRoles: UserRole[] = ['client_admin', 'client_fo_user', 'client_bo_user', 'client_fo_bo_user', 'client_ro_user', 'client_acc_user'];
      if (validRoles.includes(extractedRole as UserRole)) {
        role = extractedRole as UserRole;
      }
    } else if (firebaseUser.email?.includes('@bancoabc.cl')) {
      role = 'bank_admin';
    }

    // Extract organization info from profile or create fallback
    let organization: OrganizationInfo | undefined;
    if (profile?.organization) {
      organization = {
        id: profile.organization.id,
        name: profile.organization.name,
        type: profile.organization.type as 'client' | 'bank'
      };
    } else {
      // Fallback organization info based on user ID for development
      organization = {
        id: firebaseUser.uid, // Use user UID as organization ID
        name: firebaseUser.email?.split('@')[0] || 'Unknown Organization',
        type: role === 'bank_admin' ? 'bank' : 'client'
      };
    }

    return {
      id: firebaseUser.uid,
      name: profile?.fullName || firebaseUser.displayName || firebaseUser.email?.split('@')[0] || 'User',
      email: firebaseUser.email || '',
      role,
      profile,
      organization,
      moduleRoles: profile?.moduleRoles,
    };
  };

  const login = async (email: string, password: string) => {
    setLoading(true);
    try {
      // Use Firebase Auth to sign in
      await signInWithEmailAndPassword(auth, email, password);

      // User will be set automatically by the onAuthStateChanged listener

    } catch (error: any) {
      // MFA challenge — surface resolver to Login.tsx
      if (error.code === 'auth/multi-factor-auth-required') {
        setLoading(false);
        const resolver = getMultiFactorResolver(auth, error);
        throw { code: 'mfa-required', resolver };
      }

      setLoading(false);
      logger.error('Login error:', error);

      // Provide user-friendly error messages
      if (error.code === 'auth/user-not-found' || error.code === 'auth/wrong-password') {
        throw new Error('Invalid email or password');
      } else if (error.code === 'auth/invalid-credential') {
        throw new Error('Invalid credentials');
      } else {
        throw new Error('Login failed. Please try again.');
      }
    }
  };

  const completeMfaLogin = async (resolver: MultiFactorResolver, otpCode: string) => {
    setLoading(true);
    try {
      const hint = resolver.hints[0]; // TOTP factor
      const assertion = TotpMultiFactorGenerator.assertionForSignIn(hint.uid, otpCode);
      await resolver.resolveSignIn(assertion);
      // onAuthStateChanged will fire and load profile as normal
    } catch (error) {
      setLoading(false);
      throw error;
    }
  };

  const logout = async () => {
    try {
      await signOut(auth);
      // User will be set to null automatically by the onAuthStateChanged listener
    } catch (error) {
      logger.error('Logout error:', error);
    }
  };

  const handleForcePasswordChanged = async () => {
    logger.log('[AuthContext] Forced password change completed, refreshing profile');
    try {
      const profileResponse = await userService.getMyProfile();
      const updatedProfile = profileResponse.data;

      if (auth.currentUser) {
        const updatedUser = mapFirebaseUserToUser(auth.currentUser, updatedProfile);
        setUser(updatedUser);
      }
    } catch (error) {
      logger.error('[AuthContext] Error refreshing profile after password change:', error);
    }
    // The useEffect will close the modal when mustChangePassword becomes false
  };

  const value = {
    user,
    loading,
    login,
    completeMfaLogin,
    logout
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
      <MfaEnrollment
        open={showMfaEnrollment}
        onClose={() => {
          setShowMfaEnrollment(false);
          setMfaEnrollmentForced(false);
        }}
        forced={mfaEnrollmentForced}
      />
      <ForcePasswordChangeModal
        open={showForcePasswordChange}
        onPasswordChanged={handleForcePasswordChanged}
        onLogout={logout}
      />
      <SessionTimeoutModal
        open={showWarning}
        reason={hookTimeoutReason}
        secondsRemaining={secondsRemaining}
        onExtend={extendSession}
        onLogout={handleLogoutFromModal}
      />
    </AuthContext.Provider>
  );
};